import { Button, DatePicker, Input } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SortIcon from "../../../../assets/icons/SortIcon";
import ManageHoliday from "../../../../components/Settings/Holiday/ManageHoliday";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useDeleteHolidayMutation,
  useGetHolidaysQuery,
} from "../../../../features/holiday/holidayApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate, getTimeDurationInDays } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { GetHolidayParams, HolidayData } from "../../../../types";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray } from "../../../../utils/utils";
import { officeSettingsTabList } from "./officeSettingsTabList";

export default function Holiday() {
  const { open, handleClose, handleOpen } = useModal<string, "CONFIRM">();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    keyword,
    handleSearch,
    filter,
    handleFilter,
  } = useTable<GetHolidayParams>({
    defaultFilter: {
      year: new Date().getFullYear(),
    },
  });
  const { isLoading, isError, error, data } = useGetHolidaysQuery({
    params: {
      ...filter,
      search: keyword,
    },
  });

  const [
    deleteHoliday,
    {
      isLoading: isDeleteHolidayLoading,
      isError: isDeleteHolidayError,
      error: deleteHolidayError,
      isSuccess: isDeleteHolidaySuccess,
    },
  ] = useDeleteHolidayMutation();

  useError(isError, error, "Get holiday data fail");
  useError(isDeleteHolidayError, deleteHolidayError, "Holiday delete fail");

  const handleDeleteHoliday = () => {
    if (open?.data) {
      deleteHoliday({
        id: Number(open?.data),
      });
    }
  };

  const columns: ColumnsType<HolidayData> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Date",
      sorter: (a, b) =>
        new Date(a?.start_date)?.getTime() - new Date(b?.start_date)?.getTime(),
      sortIcon: () => <SortIcon />,
      render: (val) =>
        val?.start_date || val?.end_date ? (
          <>
            {formatDate(val?.start_date)}

            {val?.end_date &&
            formatDate(val?.start_date) !== formatDate(val?.end_date)
              ? ` - ${formatDate(val?.end_date)}`
              : ""}
          </>
        ) : (
          "n/a"
        ),
    },
    {
      title: "Day",
      sorter: (a, b) => {
        const daysA =
          a?.start_date && a?.end_date
            ? getTimeDurationInDays(a.start_date, a.end_date) + 1
            : a?.start_date
            ? 1
            : 0;

        const daysB =
          b?.start_date && b?.end_date
            ? getTimeDurationInDays(b.start_date, b.end_date) + 1
            : b?.start_date
            ? 1
            : 0;

        return daysA - daysB;
      },
      sortIcon: () => <SortIcon />,
      render: (val) => (
        <>
          {val?.start_date && val?.end_date
            ? getTimeDurationInDays(val?.start_date, val?.end_date) + 1
            : val?.start_date
            ? 1
            : 0}{" "}
          day
        </>
      ),
    },
    {
      title: "Holiday",
      dataIndex: "title",
      sorter: (a, b) => a?.title?.localeCompare(b?.title),
      sortIcon: () => <SortIcon />,
      render: (val) => val ?? "n/a",
    },

    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      render: (val) => (
        <div className="flex justify-center space-x-2">
          <Button
            icon={<EditIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "UPDATE",
                data: val,
              })
            }
          />
          <Button
            icon={<DeleteIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "CONFIRM",
                data: val,
              })
            }
          />
        </div>
      ),
    },
  ];

  // success toast message
  useEffect(() => {
    if (isDeleteHolidaySuccess) {
      toast("success", "Holiday successfully deleted!");
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteHolidaySuccess]);

  return (
    <SidebarLayout title="Office Settings" tabList={officeSettingsTabList}>
      {(open.type === "ADD" || open.type === "UPDATE") && (
        <ManageHoliday
          open={open.open}
          data={open?.data}
          type={open?.type}
          handleClose={handleClose}
        />
      )}

      {open?.type === "CONFIRM" ? (
        <ConfirmModal
          open={open?.open}
          handleClose={handleClose}
          handleOk={handleDeleteHoliday}
          confirmTitle="Delete"
          okText="Confirm"
          okLoading={isDeleteHolidayLoading}
        />
      ) : null}

      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.holidays)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              onChange={handleSearch}
            />
            <DatePicker
              style={{ width: 130 }}
              suffixIcon={<DateIcon />}
              picker="year"
              placeholder="Select Year"
              value={filter?.year ? dayjs().year(filter.year) : null}
              onChange={(date) => handleFilter("year", date?.year())}
            />

            <Button
              type="primary"
              className="middle-button"
              icon={<AddIcon />}
              onClick={() =>
                handleOpen({
                  type: "ADD",
                })
              }
            >
              Add New Holiday
            </Button>
          </>
        }
      />
    </SidebarLayout>
  );
}
